import { Component, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  KitchenValidationProjectResumeModel,
  ValidationProjectResumeTableModel,
} from 'src/app/models/configurators-2d/applications/kitchen/validation/kitchen-validation-models';
import { CustomerService } from 'src/app/services/customer.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { TunnelService } from 'src/app/services/tunnel.service';

@Component({
  selector: 'ng-kitchen-validation-project-resume',
  templateUrl: './validation-project-resume.component.html',
})
export class KitchenValidationProjectResumeComponent implements OnInit, OnDestroy {
  @Input() model$: Observable<KitchenValidationProjectResumeModel>;
  private _modelSubscription: Subscription;

  @Input() loading$: Observable<boolean>;

  public model: KitchenValidationProjectResumeModel;
  public loading: boolean;

  public readonly validationProjectResumeWithInstallation: BehaviorSubject<ValidationProjectResumeTableModel> =
    new BehaviorSubject<ValidationProjectResumeTableModel>(undefined);

  public readonly validationProjectResumeWithoutInstallation: BehaviorSubject<ValidationProjectResumeTableModel> =
    new BehaviorSubject<ValidationProjectResumeTableModel>(undefined);

  constructor(private readonly injector: Injector) {}

  ngOnInit(): void {
    this.subscribeToObservables();
    this.resetFidelityCardPoint();
  }

  ngOnDestroy(): void {
    this._modelSubscription?.unsubscribe();
  }

  private subscribeToObservables() {
    this._modelSubscription = this.model$.subscribe(model => {
      if (model) {
        this.model = model;
        this.buildValidationProjectResumeTableModel();
      }
    });
  }

  private buildValidationProjectResumeTableModel() {
    this.buildValidationProjectResumeWithoutInstallation();
    this.buildValidationProjectResumeWithInstallation();
  }

  private buildValidationProjectResumeWithInstallation() {
    const customerService = this.injector.get(CustomerService);
    const tunnelService = this.injector.get(TunnelService);
    const validationProjectResumeWithInstallation: ValidationProjectResumeTableModel = {
      withInstallation: true,
      pricingWithInstallation: this.model.kitchenPricing?.withInstallation,
      workbenchInstallation: this.model.kitchenPricing?.workbenchInstallation,
      isLess2Years: this.model.isLess2Years,
      fidelityAdvantageValidationModel: {
        amount: this.getDiscountedPriceFromKitchenPricing(true, 'prixTotal'),
        customer: customerService.userDatas,
      },
      interest:
        tunnelService.kitchenTunnelData.fundingWithInstallation?.interest ??
        tunnelService.kitchenTunnelData?.totalFundingCostWithInstallation,
      downPayment: tunnelService.kitchenTunnelData.downPayment,
      duration:
        tunnelService.kitchenTunnelData.fundingWithInstallation?.duration ??
        tunnelService.kitchenTunnelData?.durationWithInstallation,
      mensuality:
        tunnelService.kitchenTunnelData.fundingWithInstallation?.mensuality ??
        tunnelService.kitchenTunnelData?.mensualityWithInstallation,
      montantTotal:
        tunnelService.kitchenTunnelData.fundingWithInstallation?.totalFundingCost ??
        tunnelService.kitchenTunnelData?.totalFundingPrice,
    };

    this.validationProjectResumeWithInstallation.next(validationProjectResumeWithInstallation);
  }

  private get priceKey() {
    const tunnelService = this.injector.get(TunnelService);
    return tunnelService?.kitchenTunnelData?.isLess2Years ? 'moins2' : 'plus2';
  }

  private getDiscountedPriceFromKitchenPricing(withInstallation: boolean, attributeName: string) {
    const tunnelService = this.injector.get(TunnelService);
    if (withInstallation) {
      try {
        return tunnelService.kitchenTunnelData?.kitchenPricing['withInstallation'][this.priceKey][attributeName]
          .discountPrices[tunnelService.kitchenTunnelData?.discountValue].amount;
      } catch (err) {
        logError(`L'attribut ${attributeName} n'existe pas ! - ${err}`);
      }
    } else {
      return tunnelService.kitchenTunnelData?.kitchenPricing['withoutInstallation'][attributeName].discountPrices[
        tunnelService.kitchenTunnelData?.discountValue
      ].amount;
    }
  }

  private buildValidationProjectResumeWithoutInstallation() {
    const customerService = this.injector.get(CustomerService);
    const tunnelService = this.injector.get(TunnelService);
    const validationProjectResumeWithoutInstallation: ValidationProjectResumeTableModel = {
      withInstallation: false,
      pricingWithoutInstallation: this.model.kitchenPricing?.withoutInstallation,
      workbenchInstallation: this.model.kitchenPricing?.workbenchInstallation,
      isLess2Years: this.model.isLess2Years,
      fidelityAdvantageValidationModel: {
        amount: this.getDiscountedPriceFromKitchenPricing(false, 'prixTotal'),
        customer: customerService.userDatas,
      },
      interest:
        tunnelService.kitchenTunnelData.fundingWithoutInstallation?.interest ??
        tunnelService.kitchenTunnelData?.totalFundingCostWithoutInstallation,
      downPayment: tunnelService.kitchenTunnelData.downPayment,
      duration:
        tunnelService.kitchenTunnelData.fundingWithoutInstallation?.duration ??
        tunnelService.kitchenTunnelData?.durationWithoutInstallation,
      mensuality:
        tunnelService.kitchenTunnelData.fundingWithoutInstallation?.mensuality ??
        tunnelService.kitchenTunnelData?.mensualityWithoutInstallation,
      montantTotal:
        tunnelService.kitchenTunnelData.fundingWithoutInstallation?.totalFundingCost ??
        tunnelService.kitchenTunnelData?.totalFundingWithoutInstallationPrice,
    };

    this.validationProjectResumeWithoutInstallation.next(validationProjectResumeWithoutInstallation);
  }

  private resetFidelityCardPoint() {
    this.injector.get(TunnelService).resetFidelityCardPoint();
  }
}
